import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import * as Styled from './LoyaltyPageSeparated.styles';
import { selectors } from '../LoyaltyPage/reducer';
import { selectors as selectorsLogin } from '../LoginPage/reducer';
import * as actions from '../LoyaltyPage/actions';
import { deleteFromLocalStorage } from '../../global/helpers';

import LoyaltyHeader from './components/LoyaltyHeader';
import LoyaltyTabs, { ScreenType } from './components/LoyaltyTabs';
import LoyaltyClientScreen from './components/LoyaltyClientScreen';
import LoyaltyReceiptScreen from './components/LoyaltyReceiptScreen';
import LoyaltyApplyScreen from './components/LoyaltyApplyScreen';
import LoyaltyPaymentScreen, {
  PaymentMethod,
} from './components/LoyaltyPaymentScreen';
import LoyaltyProcessingScreen from './components/LoyaltyProcessingScreen';
import { WarningBlock } from '../LoyaltyPage/components/WarningBlock';
import LoyaltyHintCard from './components/LoyaltyHintCard';

const LoyaltyPageSeparated = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const dataUser = useSelector(selectorsLogin.userData);
  const chosenReceipt = useSelector(selectors.chosenReceipt);
  const clientInfo = useSelector(selectors.clientInfo);
  const submitOrderResult = useSelector(selectors.submitOrderResult);

  const [currentScreen, setCurrentScreen] = useState<ScreenType>('client');
  const [showWithBonuses, setShowWithBonuses] = useState<boolean>(true);
  const [hasAutoTransitioned, setHasAutoTransitioned] =
    useState<boolean>(false);

  const [orderConfirmed, setOrderConfirmed] = useState<boolean>(false);

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(
    null
  );

  const partnerPercent = useMemo(() => {
    switch (dataUser?.networkCode) {
      case 'ot_a_do_ya':
        return 5;

      default:
        return 1;
    }
  }, [dataUser]);

  const isStadaBonusesCredit = useMemo(
    () =>
      dataUser?.networkTags?.some((el: { tag_id: number }) =>
        process.env.REACT_APP_DEV_AREA ? el.tag_id === 1047 : el.tag_id === 1190
      ),
    [dataUser]
  );

  const isPartnersPayment = useMemo(
    () =>
      dataUser?.sourceTags?.some((el: { tag_id: number }) =>
        process.env.REACT_APP_DEV_AREA ? el.tag_id === 1048 : el.tag_id === 1192
      ),
    [dataUser]
  );

  // Очищение состояния
  const resetAllState = useCallback(() => {
    setShowWithBonuses(true);
    setCurrentScreen('client');
    setHasAutoTransitioned(false);
    setOrderConfirmed(false);
    setPaymentMethod(null);
    deleteFromLocalStorage('chosenReceipt');
    dispatch(actions.setChosenReceipt.request(null));
    dispatch(actions.resetClientInfo());
    dispatch(actions.resetCurrentReceipt());
    dispatch(actions.resetApplyBonuses());
    dispatch(actions.resetSubmitOrder());
  }, [dispatch]);

  // сверка токена аптеки с уточкой
  useEffect(() => {
    if (dataUser?.sourceCode) {
      dispatch(
        actions.getHealthStatus.request({
          source_code: dataUser?.sourceCode,
        })
      );
    }
  }, [dataUser]);

  // При смене клиента: сбросить чек, бонусы, флаги
  useEffect(() => {
    // Клиент сброшен — очистить всё связанное с чеком
    if (!clientInfo) {
      deleteFromLocalStorage('chosenReceipt');
      dispatch(actions.setChosenReceipt.request(null));
      dispatch(actions.resetApplyBonuses());
      dispatch(actions.resetSubmitOrder());
      setShowWithBonuses(true);
      setHasAutoTransitioned(false);
      setOrderConfirmed(false);
      setPaymentMethod(null);
    }
  }, [clientInfo, dispatch]);

  // Polling чеков — на вкладках receipt, apply, payment и processing
  const needsPolling =
    currentScreen === 'receipt' ||
    currentScreen === 'apply' ||
    currentScreen === 'payment' ||
    currentScreen === 'processing';

  const getReceiptInfo = useCallback(() => {
    dispatch(actions.getCurrentReceipt.request());
  }, [dispatch]);

  useEffect(() => {
    if (dataUser && needsPolling) {
      getReceiptInfo();
    }
  }, [dataUser, clientInfo, needsPolling]);

  useEffect(() => {
    if (!dataUser || !needsPolling) return;
    const intervalId = setInterval(() => {
      getReceiptInfo();
    }, 5000);
    return () => clearInterval(intervalId);
  }, [dataUser, clientInfo, needsPolling]);

  // Очищение состояния после успешного заказа
  useEffect(() => {
    if (!chosenReceipt?.items?.length && submitOrderResult) {
      resetAllState();
    }
  }, [chosenReceipt, submitOrderResult]);

  // Табы навигации
  const handleScreenChange = useCallback((screen: ScreenType) => {
    setCurrentScreen(screen);
  }, []);

  // Авто-переход после добавления клиента (только один раз)
  const handleClientAdded = useCallback(() => {
    if (!hasAutoTransitioned) {
      setHasAutoTransitioned(true);
      setCurrentScreen('receipt');
    }
  }, [hasAutoTransitioned]);

  // Переход на экран выбора оплаты после подтверждения в ApplyScreen
  const handleOrderConfirmed = useCallback(() => {
    if (!isPartnersPayment) return;
    setOrderConfirmed(true);
    setCurrentScreen('payment');
  }, [isPartnersPayment]);

  // Клиент/фармацевт выбрал способ оплаты → переходим к обработке
  const handlePaymentMethodSelected = useCallback((method: PaymentMethod) => {
    setPaymentMethod(method);
    setCurrentScreen('processing');
  }, []);

  // Вернуться с экрана обработки к выбору способа оплаты
  const handleBackToPaymentSelection = useCallback(() => {
    setPaymentMethod(null);
    setCurrentScreen('payment');
  }, []);

  // Отображение текущего экрана
  const renderScreen = () => {
    switch (currentScreen) {
      case 'client':
        return (
          <LoyaltyClientScreen
            onClientAdded={handleClientAdded}
            isStadaBonusesCredit={isStadaBonusesCredit}
            isPartnersPayment={isPartnersPayment}
            onScreenChange={handleScreenChange}
          />
        );
      case 'receipt':
        return (
          <LoyaltyReceiptScreen
            showWithBonuses={showWithBonuses}
            setShowWithBonuses={setShowWithBonuses}
            onProceedToConfirm={() => setCurrentScreen('apply')}
            partnerPercent={partnerPercent}
            isStadaBonusesCredit={isStadaBonusesCredit}
          />
        );
      case 'apply':
        return (
          <LoyaltyApplyScreen
            setShowWithBonuses={setShowWithBonuses}
            partnerPercent={partnerPercent}
            isStadaBonusesCredit={isStadaBonusesCredit}
            onOrderConfirmed={handleOrderConfirmed}
            isPartnersPayment={isPartnersPayment}
          />
        );
      case 'payment':
        return (
          <LoyaltyPaymentScreen
            onSelectPaymentMethod={handlePaymentMethodSelected}
          />
        );
      case 'processing':
        // На экран обработки нельзя попасть без выбранного способа оплаты
        if (!paymentMethod) {
          return (
            <LoyaltyPaymentScreen
              onSelectPaymentMethod={handlePaymentMethodSelected}
            />
          );
        }
        return (
          <LoyaltyProcessingScreen
            paymentMethod={paymentMethod}
            partnerPercent={partnerPercent}
            isStadaBonusesCredit={isStadaBonusesCredit}
            onBack={handleBackToPaymentSelection}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Styled.LoyaltyPageContainer>
      <WarningBlock />
      {/* Объявление о невозможности оплаты с помощью Билайна */}
      {/* <Styled.AnnouncementBlock>
        <h2>{t('LoyaltyPage.ANNOUNCEMENT.ATTENTION')}</h2>
        <span>{t('LoyaltyPage.ANNOUNCEMENT.BEELINE_NOT_ACCEPTED')}</span>
      </Styled.AnnouncementBlock> */}
      {/* Хэдер */}
      <LoyaltyHeader
        showWithBonuses={showWithBonuses}
        isStadaBonusesCredit={isStadaBonusesCredit}
      />
      {/* Кнопки навигации */}
      <LoyaltyTabs
        currentScreen={currentScreen}
        onScreenChange={handleScreenChange}
        clientAdded={!!clientInfo}
        receiptChosen={!!chosenReceipt}
        orderConfirmed={orderConfirmed}
        isPartnersPayment={isPartnersPayment}
        paymentMethodChosen={!!paymentMethod}
      />
      {/* Контент */}
      <Styled.ContentArea>
        <Styled.ScreenWithHint>
          <LoyaltyHintCard screen={currentScreen} />
          <Styled.HintMainColumn>{renderScreen()}</Styled.HintMainColumn>
        </Styled.ScreenWithHint>
      </Styled.ContentArea>{' '}
    </Styled.LoyaltyPageContainer>
  );
};

export default LoyaltyPageSeparated;
